function normalizeCertificationQuery(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatFileSize(value) {
  const size = Number(value);

  if (!Number.isFinite(size) || size <= 0) {
    return '';
  }

  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFileLabel(file, index) {
  const explicitLabel = file.label && String(file.label).trim();

  if (explicitLabel) {
    return explicitLabel;
  }

  const rawName = file.filename || '';
  const withoutExtension = rawName.replace(/\.[^.]+$/, '');
  const cleaned = withoutExtension.replace(/[-_]+/g, ' ').trim();

  if (cleaned) {
    return cleaned;
  }

  return `Certificate ${index + 1}`;
}

function createFileRowTemplate(file, index) {
  const label = formatFileLabel(file, index);
  const metaParts = [];

  if (file.extension) metaParts.push(file.extension.toUpperCase());
  if (file.size) metaParts.push(formatFileSize(file.size));

  const metaMarkup = metaParts.length
    ? `<p class="missence-certification-finder__result-file-meta">${escapeHtml(metaParts.filter(Boolean).join(' · '))}</p>`
    : '';

  return `
    <div class="missence-certification-finder__result-item">
      <div class="missence-certification-finder__result-file">
        <p class="missence-certification-finder__result-file-name">${escapeHtml(label)}</p>
        ${metaMarkup}
      </div>
      <a
        class="missence-certification-finder__result-link"
        href="${escapeHtml(file.url)}"
        target="_blank"
        rel="noopener"
        download
      >
        <span class="missence-certification-finder__result-link-label">Descargar</span>
        <span class="missence-certification-finder__result-link-icon" aria-hidden="true">
          <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9.066L2.96657 6.03343L3.57343 5.41629L5.57143 7.41429V0H6.42857V7.41429L8.42571 5.41714L9.03343 6.03343L6 9.066ZM0 12V8.53886H0.857143V11.1429H11.1429V8.53886H12V12H0Z" fill="currentColor"/>
          </svg>
        </span>
      </a>
    </div>
  `;
}

function createProductPills(products) {
  if (products.length <= 1) return '';

  return `
    <div class="missence-certification-finder__result-products">
      ${products
        .map(
          (product) =>
            `<span class="missence-certification-finder__result-pill">${escapeHtml(product)}</span>`
        )
        .join('')}
    </div>
  `;
}

function selectBestCertificationMatch(items, normalizedQuery) {
  const exactLot = items.filter((item) => item.lotNumberNormalized === normalizedQuery);
  if (exactLot.length) return exactLot[0];

  const exactProduct = items.find((item) => item.productKeys.includes(normalizedQuery));
  if (exactProduct) return exactProduct;

  const partialLot = items.find((item) => item.lotNumberNormalized.includes(normalizedQuery));
  if (partialLot) return partialLot;

  return items.find((item) => item.productKeys.some((productKey) => productKey.includes(normalizedQuery))) || null;
}

class MissenceCertificationFinder {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-certification-form]');
    this.input = section.querySelector('[data-certification-input]');
    this.feedback = section.querySelector('[data-certification-feedback]');
    this.results = section.querySelector('[data-certification-results]');
    this.resultProduct = section.querySelector('[data-certification-product]');
    this.defaultTitle = this.resultProduct ? this.resultProduct.textContent : '';
    this.subtitle = section.querySelector('[data-certification-subtitle]');
    this.defaultSubtitle = this.subtitle ? this.subtitle.textContent : '';
    this.resultBatch = section.querySelector('[data-certification-batch]');
    this.resultProducts = section.querySelector('[data-certification-products]');
    this.resultList = section.querySelector('[data-certification-files]');
    this.resultNotes = section.querySelector('[data-certification-notes]');
    this.backButton = section.querySelector('[data-certification-back]');
    this.items = this.readItems();

    if (!this.form || !this.input) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.search();
    });

    this.backButton?.addEventListener('click', () => {
      this.resetView();
    });
  }

  readItems() {
    const source = this.section.querySelector('[data-certification-source]');

    if (!source) return [];

    try {
      const parsed = JSON.parse(source.textContent);

      return parsed.map((item) => ({
        ...item,
        lotNumberNormalized: normalizeCertificationQuery(item.lotNumberNormalized || item.lotNumber),
        productKeys: (item.products || []).map((product) => normalizeCertificationQuery(product)),
      }));
    } catch (error) {
      console.error('Failed to parse certification dataset.', error);
      return [];
    }
  }

  setFeedback(message, isError = false) {
    if (!this.feedback) return;

    this.feedback.textContent = message;
    this.feedback.classList.toggle('is-error', isError);
  }

  hideResults() {
    if (!this.results) return;
    this.results.classList.remove('is-visible');
    this.section.classList.remove('is-results-view');
  }

  showResults(item) {
    if (!this.results) return;

    this.resultProduct.textContent = item.primaryProduct || item.lotNumber;
    this.resultBatch.textContent = `Número de lote: ${item.lotNumber}`;
    this.resultBatch.hidden = false;
    this.resultProducts.innerHTML = createProductPills(item.products || []);
    this.resultList.innerHTML = (item.files || []).map(createFileRowTemplate).join('');

    if (item.notes) {
      this.resultNotes.textContent = item.notes;
      this.resultNotes.hidden = false;
    } else {
      this.resultNotes.hidden = true;
      this.resultNotes.textContent = '';
    }

    this.results.classList.add('is-visible');
    this.section.classList.add('is-results-view');
    if (this.backButton) this.backButton.hidden = false;
  }

  resetView() {
    this.hideResults();
    this.setFeedback('');
    if (this.resultProduct) this.resultProduct.textContent = this.defaultTitle;
    if (this.subtitle) this.subtitle.textContent = this.defaultSubtitle;
    if (this.resultBatch) {
      this.resultBatch.hidden = true;
      this.resultBatch.textContent = '';
    }
    if (this.resultProducts) this.resultProducts.innerHTML = '';
    if (this.resultList) this.resultList.innerHTML = '';
    if (this.resultNotes) {
      this.resultNotes.hidden = true;
      this.resultNotes.textContent = '';
    }
    if (this.backButton) this.backButton.hidden = true;
    this.input?.focus();
  }

  search() {
    const normalizedQuery = normalizeCertificationQuery(this.input.value);

    if (!normalizedQuery) {
      this.resetView();
      this.setFeedback('Introduce un número de lote o producto para continuar.', true);
      return;
    }

    const match = selectBestCertificationMatch(this.items, normalizedQuery);

    if (!match) {
      this.resetView();
      this.setFeedback('No hemos encontrado certificados para esta búsqueda.', true);
      return;
    }

    if (!match.files || !match.files.length) {
      this.resetView();
      this.setFeedback('Hemos encontrado el lote, pero todavía no tiene archivos disponibles.', true);
      return;
    }

    this.showResults(match);
    this.setFeedback(`Se han encontrado ${match.files.length} archivo(s) para este lote.`);
  }
}

document.querySelectorAll('[data-missence-certification-finder]').forEach((section) => {
  new MissenceCertificationFinder(section);
});
